import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchLegacyProtection from '../../core/apiHooks/useFetchLegacyProtection';
import useWarranties from '../WarrantyOptions/useWarranties';
import { ACCESSORIES, CHARGING, WHEELS } from '../ConfigureBap/utils/constants';
import { selectCurrentVehicle, selectCurrentVehicleAccessoryKeys, selectCurrentVehicleProtectionKeys, } from '../../core/reducers/currentVehicle';
import { selectFlattenedCurrentVehicleFromVehicles } from '../../core/reducers/vehicles';
import { selectPrimaryCalculatorIsPaymentMethodCash, selectPrimaryCalculatorPaymentFrequency, selectPrimaryCalculatorPaymentMethod, selectPrimaryCalculatorTermAndRate, } from '../../core/reducers/financialCalculators';
import { useAccessoriesFetch } from '../../core/apiHooks/useAccessoriesFetch';
import { useAppSelector } from '../../core/utils';
import { useFetchCurrentVehiclePayment } from '../../core/apiHooks/useFetchPayment';
import { useLanguage } from '../../utils/sitecoreContext';
const useCustomizationSummary = () => {
    var _a, _b, _c, _d, _e, _f;
    const language = useLanguage();
    const { t } = useTranslation();
    const vehicle = useAppSelector(selectFlattenedCurrentVehicleFromVehicles);
    const paymentFrequency = useAppSelector(selectPrimaryCalculatorPaymentFrequency);
    const paymentMethod = useAppSelector(selectPrimaryCalculatorPaymentMethod);
    const paymentTerm = useAppSelector(selectPrimaryCalculatorTermAndRate);
    const accessoryKeys = useAppSelector(selectCurrentVehicleAccessoryKeys);
    const protectionKeys = useAppSelector(selectCurrentVehicleProtectionKeys);
    const { protectionAddOnKeys } = useAppSelector(selectCurrentVehicle);
    const currentPaymentMethodCash = useAppSelector(selectPrimaryCalculatorIsPaymentMethodCash);
    const accessoriesData = useAccessoriesFetch(vehicle.transmissionDetIdentifier);
    const protectionsData = useFetchLegacyProtection(paymentMethod);
    const { showLoader: warrantyLoader, showError: warrantyError, isUninitialized: isWarrantyUninitialized, selectedWarranty, } = useWarranties();
    const paymentData = useFetchCurrentVehiclePayment();
    const availableAccessories = (accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.data) || [];
    const exteriorColorPricing = (_b = (_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _a === void 0 ? void 0 : _a.exteriorColors) === null || _b === void 0 ? void 0 : _b.find(c => c.key === (vehicle === null || vehicle === void 0 ? void 0 : vehicle.exteriorColourKey));
    const exteriorColorHasPriceMarkup = !!(exteriorColorPricing === null || exteriorColorPricing === void 0 ? void 0 : exteriorColorPricing.msrpMarkup);
    const interiorColorPricing = (_c = exteriorColorPricing === null || exteriorColorPricing === void 0 ? void 0 : exteriorColorPricing.interiorColours) === null || _c === void 0 ? void 0 : _c.find(c => c.key === (vehicle === null || vehicle === void 0 ? void 0 : vehicle.interiorColourKey));
    const interiorColorHasPriceMarkup = !!(interiorColorPricing === null || interiorColorPricing === void 0 ? void 0 : interiorColorPricing.msrpMarkup);
    const showError = (protectionsData === null || protectionsData === void 0 ? void 0 : protectionsData.isError) || warrantyError || !!(paymentData === null || paymentData === void 0 ? void 0 : paymentData.error);
    const hideAccessories = (_d = accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.isError) !== null && _d !== void 0 ? _d : false;
    const isUninitialized = (accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.isUninitialized) ||
        (protectionsData === null || protectionsData === void 0 ? void 0 : protectionsData.isUninitialized) ||
        (paymentData === null || paymentData === void 0 ? void 0 : paymentData.isUninitialized) ||
        isWarrantyUninitialized;
    const showLoader = isUninitialized ||
        (accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.isLoading) ||
        warrantyLoader ||
        (protectionsData === null || protectionsData === void 0 ? void 0 : protectionsData.isLoading) ||
        (paymentData === null || paymentData === void 0 ? void 0 : paymentData.isLoading);
    let accessoriesTotalPrice = 0;
    let wheelsTotalPrice = 0;
    let chargingTotalPrice = 0;
    let protectionsTotalPrice = 0;
    const exteriorTotalPrice = exteriorColorHasPriceMarkup ? exteriorColorPricing === null || exteriorColorPricing === void 0 ? void 0 : exteriorColorPricing.msrpMarkup : 0;
    const interiorTotalPrice = interiorColorHasPriceMarkup ? interiorColorPricing === null || interiorColorPricing === void 0 ? void 0 : interiorColorPricing.msrpMarkup : 0;
    const hasChargingAccessories = availableAccessories.some(a => a.category.id === CHARGING);
    const hasWheelAccessories = availableAccessories.some(a => a.category.id === WHEELS);
    const formatPriceWithCurrentLanguage = useMemo(() => (price) => formatPrice(price, language, 2), [language]);
    const selectedAccessories = (_e = accessoriesData === null || accessoriesData === void 0 ? void 0 : accessoriesData.data) === null || _e === void 0 ? void 0 : _e.filter(a => (accessoryKeys === null || accessoryKeys === void 0 ? void 0 : accessoryKeys.includes(a.businessKey)) || a.isIncluded).map(accessory => {
        var _a, _b;
        const accessoryPayment = (_b = (_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _a === void 0 ? void 0 : _a.accessories) === null || _b === void 0 ? void 0 : _b.find(a => a.key === accessory.businessKey);
        return {
            ...accessory,
            price: accessory.isIncluded ? 0 : accessoryPayment === null || accessoryPayment === void 0 ? void 0 : accessoryPayment.price,
            payment: accessoryPayment === null || accessoryPayment === void 0 ? void 0 : accessoryPayment.frequencyPayment,
            priceText: formatPriceWithCurrentLanguage(accessoryPayment === null || accessoryPayment === void 0 ? void 0 : accessoryPayment.price),
            paymentText: currentPaymentMethodCash
                ? ''
                : `${formatPriceWithCurrentLanguage(accessoryPayment === null || accessoryPayment === void 0 ? void 0 : accessoryPayment.frequencyPayment)}/${t(`Shared.Common.${paymentFrequency}OptionLabel`)}`,
            imageSrc: accessory.thumbnailAssetUrl,
            imageAlt: accessory === null || accessory === void 0 ? void 0 : accessory.name,
            imageFallbackSrc: accessory === null || accessory === void 0 ? void 0 : accessory.defaultThumbnailAssetUrl,
        };
    });
    const selectedProtections = (_f = protectionsData === null || protectionsData === void 0 ? void 0 : protectionsData.data) === null || _f === void 0 ? void 0 : _f.filter(p => protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(p.key)).map(protection => {
        var _a, _b;
        const protectionPayment = (_b = (_a = paymentData === null || paymentData === void 0 ? void 0 : paymentData.data) === null || _a === void 0 ? void 0 : _a.protections) === null || _b === void 0 ? void 0 : _b.find(p => p.key === protection.key);
        protectionsTotalPrice += (protectionPayment === null || protectionPayment === void 0 ? void 0 : protectionPayment.defaultAmount) || 0;
        let addOnLabel = '';
        if (protection.addOn) {
            addOnLabel = (protectionAddOnKeys === null || protectionAddOnKeys === void 0 ? void 0 : protectionAddOnKeys.includes(protection.key))
                ? t('Pages.Bap.protectionAddonIncludedLabel')
                : t('Pages.Bap.protectionAddonExcludedLabel');
        }
        return {
            ...protection,
            name: protection.title,
            price: protectionPayment === null || protectionPayment === void 0 ? void 0 : protectionPayment.defaultAmount,
            payment: protectionPayment === null || protectionPayment === void 0 ? void 0 : protectionPayment.frequencyPayment,
            priceText: formatPriceWithCurrentLanguage(protectionPayment === null || protectionPayment === void 0 ? void 0 : protectionPayment.defaultAmount),
            paymentText: currentPaymentMethodCash
                ? ''
                : `${formatPriceWithCurrentLanguage(protectionPayment === null || protectionPayment === void 0 ? void 0 : protectionPayment.frequencyPayment)}/${t(`Shared.Common.${paymentFrequency}OptionLabel`)}`,
            imageSrc: protection.assetUrl,
            imageAlt: protection === null || protection === void 0 ? void 0 : protection.title,
            imageFallbackSrc: protection === null || protection === void 0 ? void 0 : protection.assetUrl,
            addOnLabel,
            paymentTerm: (protection === null || protection === void 0 ? void 0 : protection.terms) && paymentTerm,
        };
    });
    const accessories = [];
    const wheels = [];
    const charging = [];
    selectedAccessories === null || selectedAccessories === void 0 ? void 0 : selectedAccessories.forEach(a => {
        var _a;
        const categoryId = (_a = a === null || a === void 0 ? void 0 : a.category) === null || _a === void 0 ? void 0 : _a.id;
        const price = (a === null || a === void 0 ? void 0 : a.price) || 0;
        if (categoryId === ACCESSORIES) {
            accessories.push(a);
            accessoriesTotalPrice += price;
        }
        else if (categoryId === WHEELS) {
            wheels.push(a);
            wheelsTotalPrice += price;
        }
        else if (categoryId === CHARGING) {
            charging.push(a);
            chargingTotalPrice += price;
        }
    });
    return {
        accessories,
        accessoriesTotalPriceText: formatPriceWithCurrentLanguage(accessoriesTotalPrice),
        wheels,
        wheelsTotalPriceText: formatPriceWithCurrentLanguage(wheelsTotalPrice),
        hasWheelAccessories,
        charging,
        chargingTotalPriceText: formatPriceWithCurrentLanguage(chargingTotalPrice),
        hasChargingAccessories,
        exteriorColorHasPriceMarkup,
        exteriorColorTotalPriceText: formatPriceWithCurrentLanguage(exteriorTotalPrice),
        exteriorColorPriceText: formatPrice(exteriorColorPricing === null || exteriorColorPricing === void 0 ? void 0 : exteriorColorPricing.msrpMarkup),
        exteriorColorPaymentText: currentPaymentMethodCash
            ? ''
            : `${formatPriceWithCurrentLanguage(exteriorColorPricing === null || exteriorColorPricing === void 0 ? void 0 : exteriorColorPricing.frequencyPayment)}/${t(`Shared.Common.${paymentFrequency}OptionLabel`)}`,
        interiorColorHasPriceMarkup,
        interiorColorTotalPriceText: formatPriceWithCurrentLanguage(interiorTotalPrice),
        interiorColorPriceText: formatPrice(interiorColorPricing === null || interiorColorPricing === void 0 ? void 0 : interiorColorPricing.msrpMarkup),
        interiorColorPaymentText: currentPaymentMethodCash
            ? ''
            : `${formatPriceWithCurrentLanguage(interiorColorPricing === null || interiorColorPricing === void 0 ? void 0 : interiorColorPricing.frequencyPayment)}/${t(`Shared.Common.${paymentFrequency}OptionLabel`)}`,
        protections: selectedProtections,
        warranty: selectedWarranty,
        protectionsTotalPriceText: formatPriceWithCurrentLanguage(protectionsTotalPrice),
        vehicle,
        showLoader,
        showError,
        hideAccessories,
    };
};
export default useCustomizationSummary;
